import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getLive, type EngineState, type LiveStatus } from './adminApi';
import { useAdminAccess } from './adminAccess';
import { usePolling } from './usePolling';
import { formatAgo, formatClock, formatCount } from './format';
import { SlowBanner, Unavailable } from './PanelState';

/** Contract admin-ui "Live panel": at most 15 s between refreshes. */
export const LIVE_INTERVAL_MS = 15000;

const ENGINE_LABEL: Record<EngineState, string> = { running: 'Running', stalled: 'Stalled', stopped: 'Stopped' };

const REASON_LABEL: Record<string, string> = {
  service_not_ready: 'Game service not ready',
  round_owner_lost: 'Round owner lock lost',
  loop_stopped: 'Round loop stopped',
  loop_stalled: 'Rounds have not progressed within the stall threshold',
  closing: 'Server is shutting down',
};

const PHASE_LABEL: Record<string, string> = {
  STARTING: 'Bets open', LAUNCHING: 'Dropping in', IN_PROGRESS: 'Round live', CRASHED: 'Whiteout',
};

const Tile: React.FC<{ label: string; className?: string; children: React.ReactNode }> = ({ label, className, children }) => (
  <div className={`admin-tile live-tile${className ? ` ${className}` : ''}`}>
    <dt>{label}</dt>
    <dd>{children}</dd>
  </div>
);

/**
 * "Is the game running, and is anyone playing right now?" (spec US1). Polls
 * /api/admin/live; a failed refresh keeps the last values and marks them stale.
 */
const LivePanel: React.FC = () => {
  const { token } = useAuth();
  const report = useAdminAccess();
  const fetcher = useCallback((signal: AbortSignal) => getLive(token, signal), [token]);
  const { data, error, updatedAt, stale, refresh } = usePolling<LiveStatus>(fetcher, LIVE_INTERVAL_MS, { onError: report });

  // A one-second clock for the relative labels ("Updated 3 s ago").
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => { if (updatedAt) setNow(Date.now()); }, [updatedAt]);

  const serverOffset = data && updatedAt ? Date.parse(data.serverTime) - updatedAt : 0;
  const lastRoundAgo = data?.lastRoundEndedAt
    ? formatAgo(now + (Number.isFinite(serverOffset) ? serverOffset : 0) - Date.parse(data.lastRoundEndedAt))
    : '—';
  const state = data?.engine.state;
  const reasons = data?.engine.reasons ?? [];
  const reasonLabels = reasons.map(reason => REASON_LABEL[reason] ?? reason);
  const stallSeconds = data ? Math.round(data.engine.stallThresholdMs / 1000) : null;

  return (
    <section className="admin-live panel" aria-label="Live status">
      <div className="panel-heading">
        <p className="section-label">Live status</p>
        <span className="admin-updated" aria-live="polite">
          {stale && updatedAt
            ? <span className="admin-stale">Stale — last updated {formatClock(updatedAt)}</span>
            : updatedAt ? `Updated ${formatAgo(now - updatedAt)}` : ''}
          {stale && <button type="button" className="admin-button" onClick={refresh}>Retry</button>}
        </span>
      </div>

      <SlowBanner error={error} />
      {!data && error && error.kind !== 'slow' && error.kind !== 'signin' && error.kind !== 'forbidden' && (
        <Unavailable message="Live status could not be loaded." onRetry={refresh} />
      )}
      {!data && !error && <p className="admin-muted">Loading live status…</p>}

      {data && state && (
        <dl className={`admin-live-grid${stale ? ' is-stale' : ''}`}>
          <Tile label="Engine" className={`live-tile-engine live-tile-${state}`}>
            <span className="engine-state">
              <span className="engine-dot" aria-hidden="true" />
              <span>{ENGINE_LABEL[state]}</span>
            </span>
            {reasonLabels.length > 0 && (
              <ul className="engine-reasons" title={reasonLabels.join('; ')}>
                {reasonLabels.map(label => <li key={label}>{label}</li>)}
              </ul>
            )}
            {state === 'stalled' && stallSeconds !== null && <span className="admin-sub">Threshold {stallSeconds} s</span>}
          </Tile>
          <Tile label="Last round"><span>{lastRoundAgo}</span></Tile>
          <Tile label="Rounds last hour"><span>{formatCount(data.roundsLastHour)}</span></Tile>
          <Tile label="Players online"><span>{formatCount(data.playersConnected)}</span></Tile>
          <Tile label="Bets this round"><span>{formatCount(data.betsThisRound)}</span></Tile>
          <Tile label="Round #">
            <span>{data.gameNumber === null ? '—' : formatCount(data.gameNumber)}</span>
            {data.phase && <span className="admin-sub">{PHASE_LABEL[data.phase] ?? data.phase}</span>}
          </Tile>
        </dl>
      )}
    </section>
  );
};

export default LivePanel;
